import { useState, useEffect } from 'react';
import { useOutletContext, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FolderOpen, Upload, Image, FileText, File, Trash2, Eye, X,
  Download, Plus, ArrowLeft, Camera, MapPin
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const fileTypeConfig = {
  photo: { icon: Camera, label: 'Photos', color: 'bg-green-100 text-green-600' },
  plan: { icon: MapPin, label: 'Plans', color: 'bg-blue-100 text-blue-600' },
  document: { icon: FileText, label: 'Documents', color: 'bg-amber-100 text-amber-600' },
  invoice: { icon: File, label: 'Factures fournisseurs', color: 'bg-purple-100 text-purple-600' },
};

export default function DocumentsPage() {
  const { user } = useOutletContext();
  const { chantierId } = useParams();
  const navigate = useNavigate();
  
  const [chantiers, setChantiers] = useState([]);
  const [selectedChantier, setSelectedChantier] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  
  const [uploadData, setUploadData] = useState({
    file: null,
    file_type: 'photo',
    description: ''
  });

  useEffect(() => {
    fetchChantiers();
  }, []);

  useEffect(() => {
    if (chantierId && chantiers.length > 0) {
      const chantier = chantiers.find(c => c.id === chantierId);
      if (chantier) {
        setSelectedChantier(chantier);
        setDocuments(chantier.documents || []);
      }
    }
  }, [chantierId, chantiers]);

  const fetchChantiers = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/batiment/chantiers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setChantiers(await response.json());
      }
    } catch (error) {
      console.error('Error fetching chantiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Detect file type
      let fileType = 'document';
      if (file.type.startsWith('image/')) fileType = 'photo';
      if (file.name.toLowerCase().includes('plan')) fileType = 'plan';
      if (file.name.toLowerCase().includes('facture')) fileType = 'invoice';
      
      setUploadData({ ...uploadData, file, file_type: fileType });
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file || !selectedChantier) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setUploading(true);
    const token = localStorage.getItem('client_token');

    try {
      // Upload file first
      const formData = new FormData();
      formData.append('file', uploadData.file);

      const uploadResponse = await fetch(`${API_URL}/api/files/upload`, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      const uploadResult = await uploadResponse.json();

      // Add document to chantier
      const params = new URLSearchParams({
        name: uploadData.file.name,
        file_url: uploadResult.file_url,
        file_type: uploadData.file_type,
        description: uploadData.description
      });

      const response = await fetch(
        `${API_URL}/api/batiment/chantiers/${selectedChantier.id}/documents?${params.toString()}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Document ajouté');
        setShowUploadModal(false);
        setUploadData({ file: null, file_type: 'photo', description: '' });
        fetchChantiers();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm('Supprimer ce document ?')) return;

    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(
        `${API_URL}/api/batiment/chantiers/${selectedChantier.id}/documents/${docId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Document supprimé');
        fetchChantiers();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredDocuments = filterType 
    ? documents.filter(d => d.file_type === filterType)
    : documents;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isImage = (url) => {
    return url?.match(/\.(jpg|jpeg|png|gif|webp|heic)$/i);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  // If no chantier selected, show list
  if (!selectedChantier) {
    return (
      <div className="space-y-6" data-testid="documents-page">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Espace documentaire</h1>
          <p className="text-rm-text-secondary">Sélectionnez un chantier</p>
        </div>

        {chantiers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 text-center">
            <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-rm-text-secondary">Aucun chantier disponible</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {chantiers.map((chantier) => (
              <div
                key={chantier.id}
                onClick={() => navigate(`/espace-client/crm-batiment/documents/${chantier.id}`)}
                className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 cursor-pointer hover:shadow-md hover:border-rm-primary/20 transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FolderOpen className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-rm-text truncate">{chantier.client_name}</h3>
                    <p className="text-sm text-rm-text-secondary truncate">{chantier.address}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {chantier.documents?.length || 0} document{(chantier.documents?.length || 0) !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Chantier selected - show documents
  return (
    <div className="space-y-6" data-testid="documents-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => {
            setSelectedChantier(null);
            navigate('/espace-client/crm-batiment/documents');
          }}
          className="p-2 hover:bg-slate-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-rm-text">{selectedChantier.client_name}</h1>
          <p className="text-rm-text-secondary">{selectedChantier.address}</p>
        </div>
        <Button onClick={() => setShowUploadModal(true)} className="bg-rm-primary">
          <Upload className="w-4 h-4 mr-2" />
          Ajouter
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setFilterType('')}
          className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
            !filterType ? 'bg-rm-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Tous ({documents.length})
        </button>
        {Object.entries(fileTypeConfig).map(([type, config]) => {
          const count = documents.filter(d => d.file_type === type).length;
          return (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors flex items-center gap-2 ${
                filterType === type ? 'bg-rm-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <config.icon className="w-4 h-4" />
              {config.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Documents Grid */}
      {filteredDocuments.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 text-center">
          <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-rm-text-secondary">Aucun document</p>
          <Button onClick={() => setShowUploadModal(true)} className="mt-4 bg-rm-primary">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un document
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredDocuments.map((doc) => {
            const config = fileTypeConfig[doc.file_type] || fileTypeConfig.document;
            const IconComponent = config.icon;

            return (
              <div
                key={doc.id}
                className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden group"
              >
                {/* Preview */}
                <div 
                  className="aspect-square bg-slate-100 relative cursor-pointer"
                  onClick={() => {
                    setPreviewDoc(doc);
                    setShowPreviewModal(true);
                  }}
                >
                  {isImage(doc.file_url) ? (
                    <img
                      src={doc.file_url}
                      alt={doc.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <IconComponent className="w-16 h-16 text-slate-300" />
                    </div>
                  )}
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button className="p-2 bg-white rounded-full hover:bg-slate-100">
                      <Eye className="w-5 h-5 text-slate-600" />
                    </button>
                    <a
                      href={doc.file_url}
                      download
                      onClick={(e) => e.stopPropagation()}
                      className="p-2 bg-white rounded-full hover:bg-slate-100"
                    >
                      <Download className="w-5 h-5 text-slate-600" />
                    </a>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(doc.id);
                      }}
                      className="p-2 bg-white rounded-full hover:bg-red-50"
                    >
                      <Trash2 className="w-5 h-5 text-red-500" />
                    </button>
                  </div>

                  {/* Type Badge */}
                  <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs ${config.color}`}>
                    {config.label}
                  </div>
                </div>

                {/* Info */}
                <div className="p-3">
                  <p className="font-medium text-sm text-rm-text truncate">{doc.name}</p>
                  {doc.description && (
                    <p className="text-xs text-rm-text-secondary truncate mt-1">{doc.description}</p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">{formatDate(doc.created_at)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowUploadModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-xl font-bold text-rm-text">Ajouter un document</h2>
              </div>

              <div className="p-6 space-y-4">
                {/* File Input */}
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-2">Fichier</label>
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                    {uploadData.file ? (
                      <div>
                        <p className="font-medium">{uploadData.file.name}</p>
                        <p className="text-sm text-slate-400">{(uploadData.file.size / 1024 / 1024).toFixed(2)} Mo</p>
                        <button
                          onClick={() => setUploadData({ ...uploadData, file: null })}
                          className="text-red-500 text-sm mt-2 hover:underline"
                        >
                          Supprimer
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <Upload className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">Cliquez pour sélectionner</p>
                        <p className="text-xs text-slate-400 mt-1">JPG, PNG, PDF jusqu'à 10 Mo</p>
                        <input
                          type="file"
                          onChange={handleFileSelect}
                          accept="image/*,.pdf,.doc,.docx"
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Type</label>
                  <select
                    value={uploadData.file_type}
                    onChange={(e) => setUploadData({ ...uploadData, file_type: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  >
                    {Object.entries(fileTypeConfig).map(([type, config]) => (
                      <option key={type} value={type}>{config.label}</option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Description (optionnel)</label>
                  <input
                    type="text"
                    value={uploadData.description}
                    onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                    placeholder="Ex: Photo avant travaux"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowUploadModal(false)}>Annuler</Button>
                <Button 
                  className="bg-rm-primary" 
                  onClick={handleUpload}
                  disabled={!uploadData.file || uploading}
                >
                  {uploading ? 'Upload...' : 'Ajouter'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview Modal */}
      <AnimatePresence>
        {showPreviewModal && previewDoc && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPreviewModal(false)}
          >
            <button
              onClick={() => setShowPreviewModal(false)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-slate-100"
            >
              <X className="w-6 h-6" />
            </button>

            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="max-w-4xl max-h-[80vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {isImage(previewDoc.file_url) ? (
                <img
                  src={previewDoc.file_url}
                  alt={previewDoc.name}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />
              ) : (
                <div className="bg-white rounded-xl p-8 text-center">
                  <FileText className="w-20 h-20 text-slate-300 mx-auto mb-4" />
                  <p className="font-medium mb-2">{previewDoc.name}</p>
                  <a
                    href={previewDoc.file_url}
                    download
                    className="inline-flex items-center gap-2 px-4 py-2 bg-rm-primary text-white rounded-lg"
                  >
                    <Download className="w-4 h-4" />
                    Télécharger
                  </a>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
